import i18next from "i18next";

import React from "react";
import {Button, Icon, Label, Popup, Rating, RatingProps} from "semantic-ui-react";
import {ShowMoreButton} from "src/components/ShowMoreButton";
import {KumaWorkFlow} from "src/components/Table/components/kuma/workflow";
import {KumaViewDto} from "src/services/Client";
import {checkboxSelection, getCurrentUserKumaRequirementVotes, getKumaRequirementTotalVotes, getWidth, headerCheckboxSelection, KUMA_COL_RESIZE, onShowMoreClick} from "./utils";
import {RegionMarketEdit} from "src/components/kuma/RegionMarketEdit";
import {CustomDropdownEditor} from "src/components/kuma/SelectEdit";
import {kefagRenderer, modelYearsRender, typefamilyRenderer, userRenderer} from "./NewKumaViewDialog";
import {TextAreaEdit} from "src/components/kuma/TextAreaEdit";
import DopDownFilter from "src/components/kuma/DropDownFilter";
import RatingFilter from "src/components/kuma/RatingFilter";
import {DerivateEdit} from "src/components/kuma/DerivateEdit";

const preparedDataSource = (allValues: any, filterKey: string, t?: i18next.TranslationFunction<any, object, string>, ns?: string) => {
    const updatedDatasource = allValues.map((item: any) => ({
        key: item.objectId,
        value: item[filterKey],
        text: t ? t(ns ? ns + item[filterKey] : item[filterKey]) : item[filterKey]
    }));

    // Use Map to filter out duplicates by the 'key' property
    const uniqueDataSource = Array.from(
        new Map(updatedDatasource.map((item: any) => [item.key, item])).values()
    );

    return uniqueDataSource; // Get unique values
};
// Flatten arrays in the specified field and extract unique values
const getUniqueValuesFromArrayField = (data: any, arrayField: any, filterKey: any, t?: i18next.TranslationFunction<any, object, string>, ns?: string) => {
    const allValues = data.flatMap((item: any) => item[arrayField] || []);
    return preparedDataSource(allValues, filterKey, t, ns);
};
const getKEFAGDatasource = (data: any, t: i18next.TranslationFunction<any, object, string>) => {
    const kefagList = data.flatMap((item: any) => item.architectureSystems.split(',') || []).map((item: any) => {return {name: item, objectId: item};});
    return preparedDataSource(kefagList, 'name', t);
};
const getDerivateDatasource = (data: any) => {
    const derivateList = data.flatMap((item: any) => item.derivates || []).map((item: any) => {return {name: item.names[0].name, objectId: item.btyp};});
    return preparedDataSource(derivateList, 'name');
};

export const getColumnDefs = (
    t: i18next.TranslationFunction<any, object, string>,
    activeTab: string | number,
    handleRate: ((requirementId: number, stars: number) => void),
    cn: string | undefined,
    onWorkFlowUpdate: (currentValue: KumaViewDto) => void,
    roles: any,
    parentProps: any) => [
        {
            field: 'objectId',
            headerName: t("KUMA_VIEW:ID"),
            width: getWidth('objectId',KUMA_COL_RESIZE),
            checkboxSelection: checkboxSelection,
            headerCheckboxSelection: headerCheckboxSelection,
            sortable: true,
            editable: false,

        },
        {
            field: 'votes',
            headerName: t("KUMA_VIEW:PRIORITY"),
            width: getWidth('priority',KUMA_COL_RESIZE),
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellRenderer: function (params: any) {
                const requirementVotes = getCurrentUserKumaRequirementVotes(params.data, cn);
                const totalVotes = getKumaRequirementTotalVotes(params.data.votes);
                if (activeTab === 0) {
                    return (
                        <>
                            <Rating
                                icon="star"
                                disabled={activeTab !== 0}
                                defaultRating={requirementVotes ? requirementVotes.stars : 0}
                                maxRating={5}
                                clearable={true}
                                onRate={(e: any, {rating}: any) => handleRate(params.data.objectId, rating)}
                            />
                            <Label color={totalVotes > 0 ? 'green' : 'red'}>
                                {totalVotes}
                            </Label>
                        </>

                    );
                }
                return (
                    <>
                        <Rating icon="star" disabled={activeTab !== 0} defaultRating={requirementVotes ? requirementVotes.stars : 0} maxRating={5} />
                        <Label color="grey">
                            {totalVotes}
                        </Label>
                    </>

                );
            },
            sortable: true,
            filterFramework: RatingFilter,
            filterParams: {
                dataSource: [
                    {key: 1, value: '1', text: 'Greater than'},
                    {key: 2, value: '2', text: 'Less than'},
                    {key: 3, value: '3', text: 'Equals'},

                ],
                filterKey: 'stars',
            }
        },
        {
            headerName: t("KUMA_VIEW:REGION"),
            field: 'regions',
            width: getWidth('region',KUMA_COL_RESIZE),
            sortable: true,
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellEditorPopup: true,
            cellEditor: (props: any) => <RegionMarketEdit api={props.api} data={props.data} t={t} parentProps={parentProps} />,
            cellRenderer: function (params: any) {
                if (params.data) {
                    if (params.data.regions.length > 1) {
                        if (parentProps.regions && params.data.regions.length === parentProps.regions.length - 1) {
                            return t("KUMA_VIEW:ALL_REGIONS");
                        }
                        return (
                            <div className="parentElement">
                                {
                                    params.data.regions.map((item: any, index: number) =>
                                        <div className="item" key={`${index}-${item.name}`} data-id={`region-${params.data.objectId}`}>
                                            {item.name}
                                        </div>
                                    )
                                }
                                {
                                    params.data.regions.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'region'}
                                        color='red'
                                        size='medium'
                                    />

                                }
                            </div>
                        );
                    } else {
                        return params.data.regions.length > 0 && params.data.regions[0].name;
                    }
                } else {
                    return '';
                }
            },
            getQuickFilterText: (params: any) => {
                const regions: any = [];
                params.data.regions.map((item: any) => regions.push(item.name));
                return regions.join();
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: [
                    {key: 1, value: 'V1', text: 'V1'},
                    {key: 2, value: 'V2', text: 'V2'},
                    {key: 3, value: 'V3', text: 'V3'},
                    {key: 4, value: 'V4', text: 'V4'},
                    {key: 5, value: 'V5', text: 'V5'},
                ],
                filterKey: 'name',
            }
        },
        {
            headerName: t("KUMA_VIEW:MARKET"),
            cellEditor: (props: any) => <RegionMarketEdit api={props.api} data={props.data} t={t} parentProps={parentProps} />,
            cellRenderer: function (params: any) {
                if (params.data) {
                    if (params.data.assignedCnrs.length > 1) {
                        if (parentProps.userCnrs && params.data.assignedCnrs.length === parentProps.userCnrs.length - 1) {
                            return t("KUMA_VIEW:ALL_MARKETS");
                        }
                        return (
                            <div className="parentElement">
                                {
                                    params.data.assignedCnrs.map((item: any, index: number) =>
                                        <div className="item" key={`${index}-${item.name}`} data-id={`market-${params.data.objectId}`}>
                                            {t("GLOBAL:" + item.cnr)}
                                        </div>
                                    )
                                }
                                {
                                    params.data.assignedCnrs.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'market'}
                                        color='red'
                                        size='medium'
                                    />

                                }
                            </div>
                        );
                    } else {
                        return params.data.assignedCnrs.length > 0 && t("GLOBAL:" + params.data.assignedCnrs[0].cnr);
                    }
                } else {
                    return '';
                }
            },
            field: 'assignedCnrs',
            width: getWidth('market',KUMA_COL_RESIZE),
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellEditorPopup: true,
            getQuickFilterText: (params: any) => {
                const cnrs: any = [];
                params.data.assignedCnrs.map((item: any) => cnrs.push(t("GLOBAL:" + item.cnr)));
                return cnrs.join();
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: getUniqueValuesFromArrayField(parentProps.rowData, 'assignedCnrs', 'cnr', t, "GLOBAL:"),
                filterKey: 'cnr',
            }
        },
        {
            field: 'title', headerName: t("KUMA_VIEW:TITLE"),
            sortable: true,
            width: getWidth('title',KUMA_COL_RESIZE),
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
        },
        {
            headerName: t("KUMA_VIEW:TYPEFAMILY"),
            sortable: true,
            field: 'typefamilies',
            width: getWidth('typefamily',KUMA_COL_RESIZE),
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellEditorPopup: true,
            cellEditor: (props: any) => (
                <CustomDropdownEditor
                    datasource={parentProps.typefamilies}
                    renderer={typefamilyRenderer}
                    multi={true}
                    t={t}
                    value={props.data.typefamilies}
                    width={getWidth('typefamily',KUMA_COL_RESIZE)}
                    onChange={(selectedValue?: any) => {
                        if (selectedValue && selectedValue.length > 0) {
                            props.data.typefamilies = selectedValue!.map((item: any) => item.selectedValue);
                        }
                    }}
                />),

            cellRenderer: function (params: any) {
                if (params.data) {
                    if (params.data.typefamilies.length > 1) {
                        return (
                            <div className="parentElement">
                                {
                                    params.data.typefamilies.map((item: any, index: number) =>
                                        <div className="item" key={`${index}-${item.name}`} data-id={`typefamily-${params.data.objectId}`}>
                                            {item.name}
                                        </div>
                                    )
                                }
                                {
                                    params.data.typefamilies.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'typefamily'}
                                        color='red'
                                        size='medium'
                                    />

                                }
                            </div>
                        );
                    } else if (params.data.typefamilies.length === 1) {
                        return t("GLOBAL:" + params.data.typefamilies[0].name);
                    } else {
                        return '';
                    }
                } else {
                    return '';
                }
            },
            getQuickFilterText: (params: any) => {
                const typefamilies: any = [];
                params.data.typefamilies.map((item: any) => typefamilies.push(t("GLOBAL:" + item.name)));
                return typefamilies.join();
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: getUniqueValuesFromArrayField(parentProps.rowData, 'typefamilies', 'name'),
                filterKey: 'name',
            }
        },
        {
            headerName: t("KUMA_VIEW:KEFAG"),
            cellEditorPopup: true,
            cellEditor: (props: any) => (
                <CustomDropdownEditor
                    datasource={parentProps.kumaKefagList}
                    renderer={kefagRenderer}
                    multi={true}
                    t={t}
                    width={getWidth('kefag',KUMA_COL_RESIZE)}
                    value={props.data.architectureSystems.split(',')}
                    onChange={(selectedValue?: any) => {
                        if (selectedValue && selectedValue.length > 0) {
                            props.data.architectureSystems = selectedValue.map((item: any) => item.selectedValue).join(',');
                        }
                    }}
                />),
            cellRenderer: function (params: any) {
                if (params.data) {
                    const kefaglist = params.data.architectureSystems.split(',');
                    if (kefaglist.length > 1) {
                        return (
                            <div className="parentElement">
                                {kefaglist.map((item: any, index: number) =>
                                    <div className="item" key={`${index}-${item}`} data-id={`kefag-${params.data.objectId}`}>{t(item)}</div>)}
                                {
                                    kefaglist.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'kefag'}
                                        color='red'
                                        size='medium'
                                    />
                                }
                            </div>
                        );
                    } else {
                        return t("KUMA_VIEW:" + params.data.architectureSystems);
                    }

                } else {
                    return '';
                }

            },
            field: 'architectureSystems',
            width: getWidth('kefag',KUMA_COL_RESIZE),
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            getQuickFilterText: (params: any) => {
                const kefaglist = params.data.architectureSystems.split(',').map((item: any) => t(item));
                return kefaglist.join();
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: getKEFAGDatasource(parentProps.rowData, t),
            }

        },
        {
            headerName: t("KUMA_VIEW:MODELYEAR"),
            sortable: true,
            field: 'modelyears',
            width: getWidth('modelyears',KUMA_COL_RESIZE),
            cellEditorPopup: true,
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellEditor: (props: any) => (
                <CustomDropdownEditor
                    datasource={parentProps.userPSLModelyears}
                    renderer={modelYearsRender}
                    multi={true}
                    width={getWidth('modelyears',KUMA_COL_RESIZE)}
                    t={t}
                    value={parentProps.userPSLModelyears.filter((item: any) => props.data.modelyears.map((p: any) => p.mj1).includes(item.mj1))}
                    onChange={(selectedValue?: any) => {
                        props.data.modelyears = selectedValue.map((item: any) => item.selectedValue);
                    }}
                />),
            cellRenderer: function (params: any) {
                if (params.data) {
                    if (params.data.modelyears.length > 1) {
                        return (
                            <div className="parentElement">
                                {
                                    params.data.modelyears.map((item: any, index: number) =>
                                        <div className="item" key={`${index}-${item.name}`} data-id={`modelyears-${params.data.objectId}`}>
                                            {`${item.mj4} (${item.mj1})`}
                                        </div>
                                    )
                                }
                                {
                                    params.data.modelyears.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'modelyears'}
                                        color='red'
                                        size='medium'
                                    />

                                }
                            </div>
                        );
                    } else if (params.data.modelyears.length === 1) {
                        return `${params.data.modelyears[0].mj4} (${params.data.modelyears[0].mj1})`;
                    } else {
                        return '';
                    }
                } else {
                    return '';
                }
            },
            getQuickFilterText: (params: any) => {
                const modelyears: any = [];
                params.data.modelyears.map((item: any) => modelyears.push(`${item.mj4} (${item.mj1})`));
                return modelyears.join();
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: getUniqueValuesFromArrayField(parentProps.rowData, 'modelyears', 'mj4'),
                filterKey: 'mj4',
            }
        },
        {
            headerName: t("KUMA_VIEW:DERIVATES"),
            sortable: true,
            field: 'derivates',
            width: getWidth('derivates',KUMA_COL_RESIZE),
            cellEditorPopup: true,
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellEditor: (props: any) => <DerivateEdit api={props.api} data={props.data} t={t} parentProps={parentProps} />,
            cellRenderer: function (params: any) {
                if (params.data) {
                    if (params.data.derivates.length > 1) {
                        return (
                            <div className="parentElement">
                                {
                                    params.data.derivates.map((item: any, index: number) =>
                                        <div className="item" key={`${index}-${item.name}`} data-id={`derivates-${params.data.objectId}`}>
                                            {`${item.names[0].name} (${item.btyp})`}
                                        </div>
                                    )
                                }
                                {
                                    params.data.derivates.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'derivates'}
                                        color='red'
                                        size='medium'
                                    />

                                }
                            </div>
                        );
                    } else if (params.data.derivates.length === 1) {
                        return `${params.data.derivates[0].names[0].name} (${params.data.derivates[0].btyp})`;
                    } else {
                        return '';
                    }
                } else {
                    return '';
                }
            },
            getQuickFilterText: (params: any) => {
                const derivates: any = [];
                params.data.derivates.map((item: any) => derivates.push(`${item.names[0].name} (${item.btyp})`));
                return derivates.join();
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: getDerivateDatasource(parentProps.rowData),
                filterKey: 'derivates',
            }
        },
        {
            field: 'detail',
            headerName: t("KUMA_VIEW:DETAILED_REQUIREMENT"),
            sortable: true,
            width: getWidth('detail',KUMA_COL_RESIZE),
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            cellEditor: (props: any) => (
                <TextAreaEdit
                    multi={true}
                    t={t}
                    value={props.data.detail}
                    onChange={(newValue: string) => {
                        props.data.detail = newValue;
                    }}
                />),
            suppressKeyboardEvent: (params: any) => {
                const {event, editing} = params;
                if (editing && event.key === 'Enter') {
                    // Suppress the default Enter key behavior
                    return true;
                }
                return false;
            },
            cellRenderer: function (params: any) {
                if (params.data && params.data.detail) {
                    const detaillist = params.data.detail.split('\n');
                    if (detaillist.length > 1) {
                        return (
                            <div className="parentElement">
                                {detaillist.map((item: any, index: number) =>
                                    <Popup
                                        content={item}
                                        key={`${index}-${item}`}
                                        trigger={<div className="item" title={item} data-id={`detail-${params.data.objectId}`}>{item}
                                        </div>}
                                    />)
                                }
                                {
                                    detaillist.length > 3 &&
                                    <ShowMoreButton
                                        id={params.data.objectId}
                                        onShowMoreClick={onShowMoreClick}
                                        t={t}
                                        type={'detail'}
                                        color='red'
                                        size='medium'
                                    />
                                }
                            </div>
                        );
                    } else {

                        return (
                            <Popup
                                content={params.data.detail}
                                trigger={<div className="item" data-id={`detail-${params.data.objectId}`}>
                                    {params.data.detail}
                                </div>}
                            />);
                    }
                }
                return '';
            },
            getQuickFilterText: (params: any) => {
                if (params.data.detail) {
                    const detaillist = params.data.detail.split('\n').map((item: any) => t(item));
                    return detaillist.join();
                }
            },
        },
        {
            field: 'status',
            headerName: t("KUMA_VIEW:WORKFLOW"),
            sortable: true,
            width: getWidth('detail',KUMA_COL_RESIZE),
            editable: false,
            cellRenderer: function (params: any) {
                return (
                    <KumaWorkFlow
                        onApprove={() => console.log('approved')}
                        t={t}
                        value={params.data}
                        onChange={onWorkFlowUpdate}
                        readOnly={false}
                        uglyMode={false}
                        rowIndex={0}
                        language=""
                        roles={roles}
                        i18n={i18next}
                    />
                );
            },
            filterFramework: DopDownFilter,
            filterParams: {
                dataSource: [
                    {key: 1, value: '1', text: 'Funnel'},
                    {key: 2, value: '2', text: 'Approved'},

                ],
                filterKey: 'status',
            },
            getQuickFilterText: (params: any) => {
                if (params.data.detail) {
                    const detaillist = params.data.detail.split('\n').map((item: any) => t(item));
                    return detaillist.join();
                }
            },
        },
        {
            headerName: t("KUMA_VIEW:ASSIGNEDTO"),
            valueGetter: function (params: any) {
                if (params.data && params.data.assignedUsers && params.data.assignedUsers.length > 0) {
                    return params.data.assignedUsers[0].name;
                } else {
                    return '';
                }
            },
            sortable: true,
            field: 'assignedUsers',
            cellEditorPopup: true,
            editable: (activeTab === 0 && parentProps.isEdit) ? true : false,
            width: getWidth('assignedUsers',KUMA_COL_RESIZE),
            cellEditor: (props: any) => (
                <CustomDropdownEditor
                    clearable={true}
                    datasource={parentProps.users}
                    renderer={userRenderer}
                    multi={false}
                    width={getWidth('assignedUsers',KUMA_COL_RESIZE)}
                    t={t}
                    value={parentProps.users.find((item: any) => props.data.assignedUsers.map((p: any) => p.objectId).includes(item.objectId))}
                    onChange={(selectedValue?: any) => {
                        if(selectedValue) {
                            props.data.assignedUsers = [selectedValue];
                        } else {
                            props.data.assignedUsers = [];
                        }
                    }}
                />),
        },
        {
            headerName: t("KUMA_VIEW:CREATEDBY"),
            valueGetter: function (params: any) {return params.data && params.data.createdBy ? params.data.createdBy : '';},
            sortable: true,
            field: 'created',
            editable: false,
            width: getWidth('created',KUMA_COL_RESIZE),
        },

    ];
export const getDefaultColDef: any = {
    editable: true,
    enableRowGroup: true,
    enablePivot: true,
    enableValue: true,
    resizable: true,
    filter: true,
    minWidth: 100,
    autoHeight: true,
};



// WEBPACK FOOTER //
// ./src/views/KumaView/KumaBasicColumns.tsx